/**
 * 中文词典 (默认语言)。
 *
 * 键命名约定:
 *   nav.*        导航 (桌面与移动两套菜单共用)
 *   common.*     跨视图通用动作/状态词
 *   auth.*       登录/注册
 *   enum.*       业务枚举 (决策/置信度/状态/市场), 经 utils/labels.ts 消费
 *   history./batch./tasks./alerts.*  各列表页列头与提示
 *   theme.*      主题切换
 * 新增视图请将主文案登记于此 (及 en.ts), 经 useI18nLite().t() 消费。
 */
export const zh: Record<string, string> = {
  // ===== 导航 (桌面平铺 + 移动分组共用) =====
  'nav.analyze': '📊 单股分析',
  'nav.batch': '📦 批量分析',
  'nav.tasks': '⚡ 任务中心',
  'nav.chat': '💬 AI 投资对话',
  'nav.watchlist': '⭐ 自选股',
  'nav.screener': '🎯 自动选股',
  'nav.stockCompare': '⚖️ 个股对比',
  'nav.funds': '🍕 基金/ETF筛选',
  'nav.macro': '🌐 宏观研判',
  'nav.market': '📈 大盘复盘',
  'nav.portfolio': '💼 持仓管理',
  'nav.alerts': '🔔 告警',
  'nav.factors': '🧪 因子看板',
  'nav.portfolioBacktest': '📈 组合回测',
  'nav.factorBacktest': '🎯 因子回测平台',
  'nav.caseLibrary': '📚 案例库',
  'nav.paperDeployments': '🧪 模拟盘部署',
  'nav.strategyLab': '🧪 策略实验室',
  'nav.retrospective': '🏆 胜率复盘',
  'nav.anomalyScanner': '⚡ 异动扫描',
  'nav.history': '📜 历史',
  'nav.autoResearch': '🧭 自动投研',
  'nav.researchOps': '🧾 证据工作台',
  'nav.settings': '⚙️ 系统设置',
  'nav.agentBacktest': '🧪 决策评估工作台',
  'nav.fundFlow': '💹 资金流',
  'nav.arbitrage': '⚖️ AH套利',
  'nav.earningsCalendar': '📅 财报日历',
  'nav.options': '🎯 期权策略计算器',
  'nav.cbond': '🪙 可转债工作台',
  'nav.sectorRrg': '🔄 行业轮动 RRG',
  'nav.thesisWorkbench': '📜 价值投资工作台',
  'nav.group.research': '🔬 投研与对比',
  'nav.group.screenerFactors': '🎯 选股与因子',
  'nav.group.macroMarket': '🌐 宏观与市场',
  'nav.group.alphaResearch': '🧪 因子与标的',
  'nav.group.strategyLab': '📈 策略与回测',
  'nav.group.executionMonitor': '⚡ 监控与执行',
  'nav.group.portfolioRisk': '💼 组合与风控',
  'nav.group.workbench': '🛠️ 投研工作台',
  'nav.admin': '管理员',
  'nav.brand': 'TickerLab 智能投资分析',

  // ===== 通用 =====
  'common.loading': '加载中...',
  'common.confirm': '确认',
  'common.cancel': '取消',
  'common.delete': '删除',
  'common.save': '保存',
  'common.refresh': '刷新',
  'common.search': '搜索',
  'common.error': '错误',
  'common.success': '成功',
  'common.warning': '警告',
  'common.minutes': '分钟',
  'common.seconds': '秒',
  'common.empty': '暂无数据',
  'common.networkError': '网络错误',
  'common.operation': '操作',
  'common.status': '状态',
  'common.actions': '操作',
  'common.createdAt': '创建时间',
  'common.symbol': '标的',
  'common.export': '导出',
  'common.retry': '重试',
  'common.value': '值',
  'common.analyze': '分析',

  // ===== 深度研究工具 (ROADMAP P1) =====
  'research.title': '深度研究工具',
  'research.tab.report': '财报抽取',
  'research.tab.sentiment': '舆情',
  'research.tab.liquidity': '流动性',
  'research.noSymbol': '该分析未识别出标的代码, 舆情/流动性需要标的',
  'research.report.upload': '拖拽或点击上传财报 PDF / Excel',
  'research.report.tip': '上传后自动抽取关键财务字段 (≤20MB)',
  'research.report.confidence': '置信度',
  'research.report.warnings': '解析警告',
  'research.report.failed': '财报解析失败',
  'research.sentiment.run': '分析近 7 天舆情',
  'research.sentiment.news': '新闻条数',
  'research.sentiment.overall': '整体情绪',
  'research.sentiment.noNews': '近期无新闻',
  'research.sentiment.failed': '舆情分析失败',
  'research.liquidity.run': '计算流动性',
  'research.liquidity.note': '指标基于合成盘口代理数据, 仅供标的间相对比较',
  'research.liquidity.score': '流动性评分',
  'research.liquidity.rating': '评级',
  'research.liquidity.adv': '日均成交额',
  'research.liquidity.turnover': '日均换手',
  'research.liquidity.amihud': 'Amihud 非流动性',
  'research.liquidity.spread': 'CS 价差',
  'research.liquidity.failed': '流动性计算失败',

  // ===== Chat RAG 模式 (ROADMAP P1) =====
  'chat.rag.mode': '知识检索模式',
  'chat.rag.placeholder': '输入问题, 检索你的历史分析决策与知识库 (Enter 发送)',
  'chat.rag.hint': '回答基于你自己的历史决策与内置知识库, 引用来源显示在答案下方',

  // ===== 组合与风控 (ROADMAP P2) =====
  'common.view': '查看',
  'risk.run': '生成风险快照',
  'risk.done': '风险快照已生成',
  'risk.failed': '风险快照生成失败',
  'risk.hint': '基于当前持仓计算 VaR / CVaR / 波动 / 回撤等组合级指标并留存快照',
  'risk.history': '历史快照',
  'risk.var95': 'VaR 95%',
  'risk.cvar95': 'CVaR 95%',
  'risk.vol': '年化波动',
  'risk.maxdd': '最大回撤',
  'risk.sharpe': '夏普',
  'risk.hhi': '集中度 HHI',
  'backtest.dialogTitle': '决策回测结果',
  'backtest.period': '回测区间',
  'backtest.failed': '决策回测失败',
  'sbhist.title': '📜 回测历史',
  'sbhist.strategy': '策略',
  'sbhist.period': '天数',
  'sbhist.annualReturn': '年化收益',
  'pref.title': '用户偏好',
  'pref.holdingDays': '典型持有天数',
  'pref.holdingDaysHint': '用于个性化决策建议与提示词补丁',
  'pref.patchPreview': '提示词补丁预览',
  'export.title': '数据导出',
  'export.note': '导出你自己的数据 (含 CSV 公式注入防护); 上限 10000 条',
  'export.failed': '导出失败',
  'export.type.portfolio': '持仓组合',
  'export.type.decisions': '分析决策',
  'export.type.backtests': '决策回测',
  'export.type.risk_snapshots': '风险快照',
  'export.type.watchlists': '自选股池',
  'export.type.chat_sessions': '对话记录',

  // ===== 登录 / 注册 =====
  'auth.brand.subtitle': 'TickerLab 智能投资决策辅助平台',
  'auth.tab.login': '账号登录',
  'auth.tab.register': '新用户注册',
  'auth.username.placeholder': '用户名 (3-64位字符)',
  'auth.email.placeholder': '电子邮箱 (选填)',
  'auth.invite.placeholder': '邀请码 (开启邀请制时必填)',
  'auth.password.placeholder': '密码',
  'auth.passwordConfirm.placeholder': '确认密码',
  'auth.submit.login': '登 录',
  'auth.submit.register': '立即注册并登录',
  'auth.warn.empty': '请输入用户名和密码',
  'auth.warn.passwordMin': '密码长度不能少于 8 位',
  'auth.warn.passwordMismatch': '两次输入的密码不一致', // NOSONAR: 界面文案, 非硬编码凭据
  'auth.success.registered': '注册成功',
  'auth.success.login': '登录成功，欢迎使用 AlphaLens',
  'auth.error.failed': '认证失败，请检查账号密码',
  'auth.toast.loggedOut': '已退出登录',
  'auth.logout': '退出登录',
  'auth.toast.langZh': '已切换到中文',
  'auth.toast.langEn': 'Switched to English',
  'auth.feature.quant': '✨ TickerLab 深度量化分析',
  'auth.feature.backtest': '📊 机构级组合回测',
  'auth.feature.cppi': '🛡️ CPPI 保本模型',

  // ===== 业务枚举 (经 utils/labels.ts 消费) =====
  'enum.decision.buy': '买入',
  'enum.decision.sell': '卖出',
  'enum.decision.hold': '观望',
  'enum.confidence.high': '高',
  'enum.confidence.medium': '中',
  'enum.confidence.low': '低',
  'enum.status.queued': '排队中',
  'enum.status.running': '运行中',
  'enum.status.completed': '已完成',
  'enum.status.failed': '已失败',
  'enum.status.ready': '就绪',
  'enum.status.evaluated': '已评估',
  'enum.status.missingData': '数据缺失',
  'enum.market.cn': 'A股',
  'enum.market.hk': '港股',
  'enum.market.us': '美股',
  'enum.quality.good': '数据完好',
  'enum.quality.degraded': '数据降级',
  'enum.quality.partial': '数据不全',
  'enum.quality.stale': '数据过期',
  'enum.quality.missing': '数据缺失',
  'enum.quality.unknown': '未知',
  'enum.phase.pre_market': '盘前',
  'enum.phase.intraday': '盘中',
  'enum.phase.lunch': '午间休市',
  'enum.phase.near_close': '临近收盘',
  'enum.phase.after_hours': '盘后',
  'enum.phase.non_trading': '休市',
  'enum.phase.unknown': '未知',
  'enum.sentiment.positive': '偏多',
  'enum.sentiment.negative': '偏空',
  'enum.sentiment.neutral': '中性',

  // ===== 历史分析 =====
  'history.title': '历史分析',
  'history.col.decision': '决策',
  'history.col.confidence': '置信度',
  'history.col.status': '状态',
  'history.col.createdAt': '提交时间',
  'history.empty': '暂无分析记录',
  'history.error.load': '加载分析历史失败',

  // ===== 批量分析 =====
  'batch.col.batch': '批次',
  'batch.col.name': '名称',
  'batch.col.total': '总数',
  'batch.col.progress': '进度 (完成/失败)',
  'batch.col.createdAt': '创建时间',
  'batch.error.load': '加载批量历史失败',
  'batch.confirm.delete': '删除该批次? 其全部子分析将一并删除, 不可恢复。',
  'batch.success.deleted': '已删除批次',
  'batch.error.delete': '删除失败',

  // ===== 任务中心 =====
  'tasks.error.load': '获取后台任务列表失败',

  // ===== 告警 =====
  'alerts.col.rule': '规则',
  'alerts.col.symbol': '标的',
  'alerts.col.type': '类型',
  'alerts.col.threshold': '阈值',
  'alerts.col.enabled': '启用',
  'alerts.col.lastTriggered': '最近触发',

  // ===== 主题 =====
  'theme.toggle.toLight': '切换到浅色',
  'theme.toggle.toDark': '切换到深色',
  'theme.switched.toDark': '已切换到深色模式',
  'theme.switched.toLight': '已切换到浅色模式',
  // ===== 市场机会发现 (ROADMAP P3) =====
  'ff.marketTitle': '大盘资金流向',
  'ff.north': '北向净流入',
  'ff.south': '南向净流入',
  'ff.main': '主力净流入',
  'ff.superLarge': '超大单',
  'ff.large': '大单',
  'ff.small': '小单',
  'ff.trend': '趋势',
  'ff.topIn': '板块净流入榜',
  'ff.topOut': '板块净流出榜',
  'ff.mainNet': '主力净额',
  'ff.changePct': '涨跌幅',
  'ff.rules': '资金流告警规则',
  'ff.addRule': '新增规则',
  'ff.indicator': '指标',
  'ff.direction': '方向',
  'ff.threshold': '阈值 (元)',
  'ff.above': '高于',
  'ff.below': '低于',
  'ff.triggered': '历史触发',
  'ff.market': '市场',
  'ff.loadFailed': '资金流加载失败',
  'ff.snapshotTitle': '当日实时资金快照',
  'ff.trendTitle': '5日资金趋势与北向研判',
  'ff.trendStatus': '综合趋势判定',
  'ff.trendInflow': '持续净流入',
  'ff.trendOutflow': '持续净流出',
  'ff.trendNeutral': '震荡整理 / 中性平衡',
  'ff.northCum5d': '5日累计北向净流入',
  'ff.northAvg5d': '5日日均北向净流入',
  'ff.consecutiveDays': '连续流入天数',
  'ff.volatility': '北向净额波动度 (StdDev)',
  'ff.samplePeriod': '有效历史样本',
  'ff.days': '天',
  'ff.trendSummaryInflow': '北向资金保持连续净流入态势，主力增配意愿显著。',
  'ff.trendSummaryOutflow': '北向资金近期呈现净流出态势，需注意大盘流动性防守。',
  'ff.trendSummaryNeutral': '北向资金多空博弈均衡，暂未形成单边持续性趋势。',
  'ah.title': 'AH 股溢价套利',
  'ah.hint': 'A/H 溢价率 = (A股价 - H股价×汇率) / (H股价×汇率); 正值代表 A 股相对溢价',
  'ah.scan': '重新扫描',
  'ah.pair': '配对',
  'ah.premium': '溢价率',
  'ah.snapshots': '扫描快照',
  'ah.pairCount': '配对数',
  'ah.avgPremium': '平均溢价',
  'ah.loadFailed': '套利数据加载失败',
  'ah.scanFailed': '套利扫描失败',
  'cal.title': '财报披露日历',
  'cal.daysAhead': '未来天数',
  'cal.allMarkets': '全部市场',
  'cal.company': '公司',
  'cal.marketCol': '市场',
  'cal.reportDate': '披露日期',
  'cal.period': '报告期',
  'cal.loadFailed': '财报日历加载失败',

  'nav.reports': '📊 投研报告',
  'rep.title': '投研报告中心',
  'rep.weekly': '生成周报',
  'rep.monthly': '生成月报',
  'rep.type': '类型',
  'rep.range': '统计区间',
  'rep.preview': '报告预览',
  'rep.loadFailed': '报告加载失败',
  'rep.genFailed': '报告生成失败',

  'audit.title': '审计日志',
  'audit.action': '操作类型',

  // ===== 自动投研管线 (ALENS-10) =====
  'autoResearch.title': '自动投研管线',
  'autoResearch.subtitle': '初筛 → 严谨性校验 → 三视角 → 对抗验证 → 强制三态结论',
  'autoResearch.symbol': '标的代码',
  'autoResearch.symbolPlaceholder': '如 sh.600519',
  'autoResearch.name': '简称',
  'autoResearch.namePlaceholder': '选填, 便于阅读',
  'autoResearch.submit': '发起投研',
  'autoResearch.running': '投研中...',
  'autoResearch.node.init': '创建',
  'autoResearch.node.dataPack': '数据打包',
  'autoResearch.node.screen': '硬指标初筛',
  'autoResearch.node.rigor': '严谨性校验',
  'autoResearch.node.multiLens': '三视角研究',
  'autoResearch.node.adversarial': '对抗验证',
  'autoResearch.node.verdict': '最终裁决',
  'autoResearch.node.report': '生成报告',
  'autoResearch.node.done': '完成',
  'autoResearch.result.title': '投研结论',
  'autoResearch.result.downloadPdf': '下载 PDF',
  'autoResearch.verdict.pass': '✅ 通过 (值得深究/入池)',
  'autoResearch.verdict.reject': '❌ 不通过 (红线否决)',
  'autoResearch.verdict.grey_zone': '🟡 灰色地带 (证据不足, 留白)',
  'autoResearch.verdict.confidence': '置信度',
  'autoResearch.verdict.score': '综合评分',
  'autoResearch.verdict.mirrorTest': '镜子测试 (5 句话)',
  'autoResearch.strategy.title': '三档策略',
  'autoResearch.strategy.aggressive': '激进',
  'autoResearch.strategy.balanced': '稳健',
  'autoResearch.strategy.conservative': '保守',
  'autoResearch.strategy.tier': '档位',
  'autoResearch.strategy.action': '动作',
  'autoResearch.strategy.priceRange': '价格区间',
  'autoResearch.strategy.position': '仓位上限',
  'autoResearch.action.buy': '买入',
  'autoResearch.action.wait': '等待',
  'autoResearch.action.avoid': '回避',
  'autoResearch.invalidation': '论点失效条件 (触发即重估)',
  'autoResearch.dataGaps': '研究留白 (未能回答的问题)',
  'autoResearch.guardrails': '结论纪律复核 (规则层改写记录)',
  'autoResearch.tab.conclusion': '结论',
  'autoResearch.tab.screening': '初筛',
  'autoResearch.tab.rigor': '数据严谨性',
  'autoResearch.tab.lenses': '三视角',
  'autoResearch.tab.adversarial': '对抗验证',
  'autoResearch.tab.report': '完整报告',
  'autoResearch.screening.rejected': '触发红线否决: ',
  'autoResearch.screening.insufficient': '数据不足: 财报期数不满足统计显著性门槛, 不进入深研',
  'autoResearch.screening.passed': '通过初筛, 未触发红线',
  'autoResearch.screening.skipped': '初筛短路, 本阶段未执行',
  'autoResearch.rigor.grade': '数据置信度',
  'autoResearch.rigor.result': '结果',
  'autoResearch.rigor.detail': '说明',
  'autoResearch.lens.bullish': '看多',
  'autoResearch.lens.neutral': '中性',
  'autoResearch.lens.bearish': '看空',
  'autoResearch.lens.name.value': '价值',
  'autoResearch.lens.name.quality': '质量',
  'autoResearch.lens.name.growth': '成长',
  'autoResearch.claim.upheld': '成立',
  'autoResearch.claim.weakened': '削弱',
  'autoResearch.claim.refuted': '被推翻',
  'autoResearch.claim.challenge': '空方反驳',
  'autoResearch.claim.evidence': '判定依据',
  'autoResearch.history.title': '历史投研',
  'autoResearch.history.verdict': '结论',
  'autoResearch.history.status': '状态',
  'autoResearch.history.time': '发起时间',
  'autoResearch.history.empty': '暂无投研记录, 输入标的代码发起第一次自动投研',
  'autoResearch.error.symbolRequired': '请输入标的代码',
  'autoResearch.error.create': '发起投研失败',
  'autoResearch.error.fetchResult': '获取投研结果失败',
  'autoResearch.error.downloadPdf': 'PDF 下载失败',
  'autoResearch.error.failed': '投研失败',

}